/**
 * 55 — SETTINGS.
 *
 * The one screen in the app that is chrome, not the record. Everything the home
 * screen deliberately does NOT have — a search box, the theme switch, the DAW
 * door, the admin knobs — was moved off the shelf and into here, so the shelf
 * can stay stupid: ten songs, drop the needle.
 *
 * Contract the router depends on (main.ts): mountSettings(root) renders into
 * `root` and returns a bare teardown — exactly like mountActivity / mountOffline.
 *
 * ── Layout, top to bottom ───────────────────────────────────────────────────
 *   1. SEARCH        — the only place you search. Ten songs client-side.
 *   2. APPEARANCE    — light/dark, moved off the home header.
 *   3. THE RECORD    — admin only: fidelity + side length. Hidden for everyone
 *                      else, and the Worker refuses it too — this is not the lock.
 *   4. THE WORKSTATION — the one door out to openDAW, moved off the home header.
 *   5. YOU           — who you are, and the way out. Quiet, at the bottom.
 *
 * Monochrome, sharp, hairline-divided. Mono section headers. No blur.
 */

import {
  getSongs,
  getAlbum,
  patchAlbum,
  logout,
  type Song,
  type Fidelity,
} from './api';
import { currentMember, type LoginMember } from './login';
import { h, clear, icon, toast, isLight, toggleTheme } from './ui';

/* ------------------------------------------------------------------- words */

/** A side of a record is minutes, not hours. Anything outside this is a typo,
 *  not a record. */
const SIDE_MIN = 5;
const SIDE_MAX = 45;

function clampMins(n: number): number {
  if (!Number.isFinite(n)) return 20;
  return Math.max(SIDE_MIN, Math.min(SIDE_MAX, Math.round(n)));
}

/** main.ts owns routing and listens for hashchange. Setting the hash IS the
 *  navigation API — same trick activity.ts uses — so a result still works with
 *  no JS and the back button behaves. */
function go(hash: string): void {
  if (location.hash === hash) return;
  location.hash = hash;
}

/** A hairline-divided section with a mono, all-caps header. Every block on the
 *  screen is one of these, so they all line up. */
function section(label: string, children: (Node | null)[]): HTMLElement {
  return h('section', { class: 'set-sec' }, [
    h('h2', { class: 'set-h', text: label }),
    ...children,
  ]);
}

/* -------------------------------------------------------------------- mount */

export function mountSettings(root: HTMLElement): () => void {
  clear(root);

  const abort = new AbortController();
  let dead = false;
  /** Every debounced save timer, swept on teardown so a late PATCH can't fire
   *  into a screen that's gone. */
  const timers: number[] = [];

  const top = h('header', { class: 'topbar' }, [h('h1', { text: 'Settings' })]);

  /* ---- 1. SEARCH ------------------------------------------------------- */

  let songs: Song[] | null = null;
  let songsError = false;

  const searchInput = h('input', {
    class: 'set-search',
    type: 'search',
    inputmode: 'search',
    autocomplete: 'off',
    autocapitalize: 'none',
    autocorrect: 'off',
    spellcheck: 'false',
    placeholder: 'Find a song by name',
    'aria-label': 'Search songs by title',
  }) as HTMLInputElement;

  const searchResults = h('div', { class: 'set-results' });

  function renderResults(): void {
    clear(searchResults);
    const q = searchInput.value.trim().toLowerCase();
    if (!q) return; // nothing typed → an empty, quiet screen, not a wall of songs

    if (songs === null) {
      // Still loading, or it failed. Only say the failure out loud — a spinner
      // for a list of nine is noise.
      if (songsError) {
        searchResults.appendChild(
          h('p', {
            class: 'set-empty',
            text: "Couldn't load the songs. This works again once you've got signal.",
          }),
        );
      }
      return;
    }

    const hits = songs.filter((s) => s.title.toLowerCase().includes(q)).slice(0, 12);
    if (hits.length === 0) {
      searchResults.appendChild(
        h('p', { class: 'set-empty', text: `Nothing called “${searchInput.value.trim()}”.` }),
      );
      return;
    }

    for (const s of hits) {
      const row = h('button', { class: 'set-result', type: 'button' }, [
        h('span', { class: 'set-result-t', text: s.title }),
        h('span', { class: 'set-result-go', 'aria-hidden': 'true', html: icon.play }),
      ]);
      row.setAttribute('aria-label', `Open ${s.title}`);
      row.addEventListener('click', () => go(`#/song/${encodeURIComponent(s.id)}`));
      searchResults.appendChild(row);
    }
  }

  searchInput.addEventListener('input', renderResults);

  const searchSec = section('Search', [
    h('p', { class: 'set-note', text: 'Jump straight to any song on the record.' }),
    h('div', { class: 'set-searchwrap' }, [
      h('span', { class: 'set-searchic', 'aria-hidden': 'true', html: icon.search }),
      searchInput,
    ]),
    searchResults,
  ]);

  /* ---- 2. APPEARANCE --------------------------------------------------- */

  const themeBtn = h('button', { class: 'set-toggle', type: 'button' }) as HTMLButtonElement;
  function paintTheme(): void {
    const light = isLight();
    clear(themeBtn);
    themeBtn.append(
      h('span', { class: 'set-toggle-ic', 'aria-hidden': 'true', html: light ? icon.moon : icon.sun }),
      h('span', { text: light ? 'Switch to dark' : 'Switch to light' }),
    );
    themeBtn.setAttribute('aria-label', light ? 'Switch to dark theme' : 'Switch to light theme');
  }
  themeBtn.addEventListener('click', () => {
    toggleTheme();
    paintTheme();
  });
  paintTheme();

  const appearanceSec = section('Appearance', [
    h('p', { class: 'set-note', text: 'A dark room for the shelf, or the daylight version.' }),
    themeBtn,
  ]);

  /* ---- 3. THE RECORD (admin only) -------------------------------------- */
  /* Built empty and hidden; filled in and revealed only once we know the member
     is an admin AND we have the album. Everyone else never sees it. */

  const recordSec = h('section', { class: 'set-sec', hidden: true });

  function buildRecord(fidelity: Fidelity, sideMinutes: number): void {
    clear(recordSec);
    recordSec.hidden = false;
    recordSec.appendChild(h('h2', { class: 'set-h', text: 'The record' }));

    /* -- Fidelity: a two-option segmented control -- */
    let currentFidelity: Fidelity = fidelity;
    const seg = h('div', { class: 'set-seg', role: 'group', 'aria-label': 'Fidelity' });
    const segBtns = new Map<Fidelity, HTMLButtonElement>();

    const opts: { value: Fidelity; label: string; size: string }[] = [
      { value: 'compressed', label: 'Compressed', size: '~200 MB' },
      { value: 'lossless', label: 'Lossless', size: '~1 GB' },
    ];

    function paintSeg(): void {
      for (const [v, b] of segBtns) {
        const on = v === currentFidelity;
        b.dataset.active = on ? '1' : '0';
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    }

    async function chooseFidelity(v: Fidelity): Promise<void> {
      if (v === currentFidelity) return;
      const prev = currentFidelity;
      currentFidelity = v; // optimistic — the tap should feel instant
      paintSeg();
      try {
        await patchAlbum({ fidelity: v });
        if (dead) return;
        toast(
          v === 'lossless'
            ? 'Saving in full quality from now on.'
            : 'Saving compressed from now on.',
        );
      } catch {
        if (dead) return;
        currentFidelity = prev;
        paintSeg();
        toast("Couldn't change that. Try again?", true);
      }
    }

    for (const o of opts) {
      const b = h('button', { class: 'set-seg-b', type: 'button', 'data-v': o.value }, [
        h('span', { class: 'set-seg-l', text: o.label }),
        h('span', { class: 'set-seg-s', text: o.size }),
      ]) as HTMLButtonElement;
      b.setAttribute('aria-label', `${o.label}, ${o.size}`);
      b.addEventListener('click', () => void chooseFidelity(o.value));
      segBtns.set(o.value, b);
      seg.appendChild(b);
    }
    paintSeg();

    recordSec.appendChild(
      h('div', { class: 'set-field' }, [
        h('label', { class: 'set-label', text: 'Fidelity' }),
        seg,
        h('p', {
          class: 'set-note',
          text: 'Compressed is smaller and every track stays editable. Lossless is the biggest, at the best fidelity.',
        }),
      ]),
    );

    /* -- Side length: a small stepper -- */
    let mins = clampMins(sideMinutes);
    const minus = h('button', {
      class: 'set-step-b',
      type: 'button',
      'aria-label': 'Fewer minutes per side',
      text: '−',
    }) as HTMLButtonElement;
    const plus = h('button', {
      class: 'set-step-b',
      type: 'button',
      'aria-label': 'More minutes per side',
      text: '+',
    }) as HTMLButtonElement;
    const readout = h('span', { class: 'set-step-n', 'aria-live': 'polite' });

    function paintMins(): void {
      readout.textContent = `${mins} min`;
      minus.disabled = mins <= SIDE_MIN;
      plus.disabled = mins >= SIDE_MAX;
    }

    let saveTimer = 0;
    function saveMins(): void {
      window.clearTimeout(saveTimer);
      // Debounced: mashing + should be ONE write, not eight.
      saveTimer = window.setTimeout(() => {
        void patchAlbum({ side_minutes: mins }).catch(() => {
          if (!dead) toast("Couldn't save the side length. Try again?", true);
        });
      }, 500);
      timers.push(saveTimer);
    }

    function bump(delta: number): void {
      const next = clampMins(mins + delta);
      if (next === mins) return;
      mins = next;
      paintMins();
      saveMins();
    }
    minus.addEventListener('click', () => bump(-1));
    plus.addEventListener('click', () => bump(1));
    paintMins();

    recordSec.appendChild(
      h('div', { class: 'set-field' }, [
        h('label', { class: 'set-label', text: 'Side length' }),
        h('div', { class: 'set-step' }, [minus, readout, plus]),
        h('p', {
          class: 'set-note',
          text: 'How many minutes fit on one side of the record — used to work out where Side A flips to Side B.',
        }),
      ]),
    );
  }

  function buildRecordError(): void {
    clear(recordSec);
    recordSec.hidden = false;
    recordSec.append(
      h('h2', { class: 'set-h', text: 'The record' }),
      h('p', {
        class: 'set-note',
        text: "Couldn't reach the record settings just now. Try again in a bit.",
      }),
    );
  }

  /* ---- 4. THE WORKSTATION ---------------------------------------------- */
  /* openDAW is a SEPARATE app on its own hostname — a real anchor, new tab, so
     the record and whatever's playing stay put. Moved off the home header. */

  const dawLink = h(
    'a',
    {
      class: 'set-daw',
      href: 'https://daw.thedevilssons.ca',
      target: '_blank',
      rel: 'noopener noreferrer',
    },
    [
      h('span', { class: 'set-daw-body' }, [
        h('span', { class: 'set-daw-t', text: 'Open openDAW' }),
        h('span', { class: 'set-daw-s', text: "the band's recording studio" }),
      ]),
      h('span', { class: 'set-daw-go', 'aria-hidden': 'true', text: '↗' }),
    ],
  );
  const workstationSec = section('The workstation', [dawLink]);

  /* ---- 5. YOU ---------------------------------------------------------- */

  const youWho = h('div', { class: 'set-you-who' });

  function paintYou(me: LoginMember | null): void {
    clear(youWho);
    youWho.appendChild(h('div', { class: 'set-you-name', text: me?.name ?? 'Signed in' }));
    if (me?.instrument) youWho.appendChild(h('div', { class: 'set-you-inst', text: me.instrument }));
  }
  paintYou(null); // a placeholder name until /api/me answers

  const signOut = h('button', {
    class: 'set-signout',
    type: 'button',
    text: 'Sign out',
  }) as HTMLButtonElement;

  async function doSignOut(): Promise<void> {
    if (signOut.disabled) return;
    signOut.disabled = true;
    signOut.textContent = 'Signing out…';
    try {
      await logout();
    } catch {
      // Even a logout the network never confirmed is handled by the reload
      // below — main.ts's boot() re-runs, finds no session, and shows the door.
    }
    location.reload();
  }
  signOut.addEventListener('click', () => void doSignOut());

  const youSec = section('You', [youWho, signOut]);

  /* ---- assemble -------------------------------------------------------- */

  root.append(
    top,
    h('div', { class: 'set' }, [searchSec, appearanceSec, recordSec, workstationSec, youSec]),
  );

  /* ---- boot: who are you, and (if admin) the record ------------------- */

  void (async () => {
    const list = getSongs(abort.signal)
      .then((s) => {
        if (dead) return;
        songs = s;
        renderResults();
      })
      .catch(() => {
        if (dead || abort.signal.aborted) return;
        songsError = true;
        renderResults();
      });

    let me: LoginMember | null = null;
    try {
      // currentMember() does the one GET /api/me and never throws; the cast is
      // safe because LoginMember only adds optional fields to Member.
      me = (await currentMember()) as LoginMember | null;
    } catch {
      me = null;
    }
    if (dead) return;

    paintYou(me);

    if (me?.role === 'admin') {
      try {
        const { album } = await getAlbum(abort.signal);
        if (dead) return;
        buildRecord(album.fidelity, album.side_minutes);
      } catch {
        if (dead || abort.signal.aborted) return;
        buildRecordError();
      }
    }

    await list; // keep the promise from dangling unhandled
  })();

  /* ---- teardown -------------------------------------------------------- */

  return function destroy(): void {
    dead = true;
    abort.abort();
    for (const t of timers) window.clearTimeout(t);
    clear(root);
  };
}
